/**
 * TeamManagementEmbedded — TEAM-SHELL-001 (Jun 2026)
 *
 * Mounts <TeamManagement embedded /> inside AdminLayout's <Outlet /> so
 * IT Manager gets Team Management inside the dark IT Operations Center
 * shell (with the tm-dark navy skin) and HR Manager gets it inside the
 * HR Portal shell. Renders at `/admin/team` — see App.js routing block.
 */
import React from 'react';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import TeamManagement from './TeamManagement';
import { getUserFromToken } from '../../utils/adminApi';

// EFFECTIVE-SCOPE-001: Admin inside the IT workspace inherits the IT Manager
// team scope. A module-level interceptor stamps the header per request —
// deterministic under StrictMode double-mounts (no lifecycle races). The
// backend honors the header ONLY for admin callers (narrow-only), so it can
// never broaden anyone's access.
const SCOPED_PATHS = ['/api/leave/', '/api/attendance/team', '/api/session-health/team-status', '/api/breaks/'];
axios.interceptors.request.use((config) => {
  try {
    const url = config.url || '';
    if (
      SCOPED_PATHS.some((p) => url.includes(p)) &&
      sessionStorage.getItem('titleflow-portal-context') === 'it' &&
      getUserFromToken()?.role === 'admin'
    ) {
      config.headers['X-Effective-Scope'] = 'it_manager';
    }
  } catch (_) { /* noop */ }
  return config;
});

const TeamManagementEmbedded = () => {
  const navigate = useNavigate();
  const user = getUserFromToken();
  const role = user?.role;

  // PORTAL-CONTEXT-001: Admin browsing from the IT Operations Center keeps
  // the dark IT theme + back-to-IT navigation.
  let itContext = false;
  try { itContext = sessionStorage.getItem('titleflow-portal-context') === 'it'; } catch (_) { /* noop */ }
  const isIT = role === 'it_manager' || (role === 'admin' && itContext);
  const effectiveScope = role === 'admin' && itContext ? 'it_manager' : null;

  if (role === 'it_support') return <Navigate to="/admin/it-dashboard" replace />;
  if (!['it_manager', 'hr_manager', 'admin'].includes(role)) {
    return <Navigate to="/admin" replace />;
  }

  const handleBack = () => {
    if (isIT) navigate('/admin/it-dashboard');
    else if (role === 'hr_manager') navigate('/admin/hr/dashboard');
    else navigate('/admin');
  };

  return (
    <TeamManagement
      currentUser={user}
      onNavigateBack={handleBack}
      embedded
      darkSkin={isIT}
      effectiveScope={effectiveScope}
    />
  );
};

export default TeamManagementEmbedded;
